import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  useMemo,
  useEffect,
  ReactNode
} from 'react';
import {
  useCampaignId,
  useCampaignContextController,
  useSpreadsheetViewType,
  bumpSpreadsheetDataVersion
} from './CampaignContext';
import {
  getActiveTargetPersonaFetchWorkflowId,
  streamTargetPersonaFetchResult
} from '../api/campaignTargetPersona';

interface TargetPersonaSearchContextType {
  isSearchRunning: boolean;
  setSearchRunning: (running: boolean) => void;
  /** Register a callback invoked when target persona search completes successfully. Returns unregister. */
  registerOnSearchComplete: (callback: () => void) => () => void;
  /** Called by stream handlers when status is COMPLETED. */
  notifySearchComplete: () => void;
}

const TargetPersonaSearchContext = createContext<
  TargetPersonaSearchContextType | undefined
>(undefined);

/** On page load, checks for active workflow via backend API and reconnects to stream if found. */
function TargetPersonaSearchRestorer() {
  const campaignId = useCampaignId();
  const { setSearchRunning, notifySearchComplete } = useTargetPersonaSearch();

  useEffect(() => {
    if (!campaignId) return;
    let cancelled = false;
    let cancelStream: (() => void) | null = null;
    getActiveTargetPersonaFetchWorkflowId(campaignId)
      .then((res) => {
        if (cancelled || !res?.workflow_id) return;
        setSearchRunning(true);
        cancelStream = streamTargetPersonaFetchResult(
          campaignId,
          res.workflow_id,
          {
            onStatus: (status) => {
              if (status === 'COMPLETED') {
                notifySearchComplete();
              }
            },
            onComplete: () => {
              if (!cancelled) setSearchRunning(false);
            },
            onError: () => {
              if (!cancelled) setSearchRunning(false);
            }
          }
        );
      })
      .catch(() => {
        if (!cancelled) setSearchRunning(false);
      });
    return () => {
      cancelled = true;
      cancelStream?.();
    };
  }, [campaignId, setSearchRunning, notifySearchComplete]);

  return null;
}

/** When target persona search completes while user is viewing leads, bump spreadsheet to refresh. */
function TargetPersonaLeadsSyncHandler() {
  const [campaignState, dispatch] = useCampaignContextController();
  const viewType = useSpreadsheetViewType();
  const { registerOnSearchComplete } = useTargetPersonaSearch();

  useEffect(() => {
    return registerOnSearchComplete(() => {
      if (viewType === 'lead') {
        bumpSpreadsheetDataVersion(dispatch);
      }
    });
  }, [registerOnSearchComplete, viewType, dispatch]);
  return null;
}

export const TargetPersonaSearchProvider: React.FC<{ children: ReactNode }> = ({
  children
}) => {
  const [isSearchRunning, setSearchRunning] = useState(false);
  const onSearchCompleteCallbacksRef = useRef<Set<() => void>>(new Set());

  const registerOnSearchComplete = useCallback((callback: () => void) => {
    onSearchCompleteCallbacksRef.current.add(callback);
    return () => {
      onSearchCompleteCallbacksRef.current.delete(callback);
    };
  }, []);

  const notifySearchComplete = useCallback(() => {
    onSearchCompleteCallbacksRef.current.forEach((cb) => cb());
  }, []);

  const value = useMemo(
    () => ({
      isSearchRunning,
      setSearchRunning,
      registerOnSearchComplete,
      notifySearchComplete
    }),
    [isSearchRunning, registerOnSearchComplete, notifySearchComplete]
  );
  return (
    <TargetPersonaSearchContext.Provider value={value}>
      <TargetPersonaSearchRestorer />
      <TargetPersonaLeadsSyncHandler />
      {children}
    </TargetPersonaSearchContext.Provider>
  );
};

export const useTargetPersonaSearch = (): TargetPersonaSearchContextType => {
  const context = useContext(TargetPersonaSearchContext);
  if (!context) {
    throw new Error(
      'useTargetPersonaSearch must be used within TargetPersonaSearchProvider'
    );
  }
  return context;
};
